import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useAuth } from './AuthContext';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

/**
 * Subscription + feature-gating context.
 * Provides: plan, billing_cycle, trial_expires_at, trial_expired, limits, features.
 * Polls /api/subscription/limits every 30s so sidebar + gates stay in sync
 * after trial expires or admin changes the plan.
 */
const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  return ctx || {
    loading: true,
    plan: null,
    billing_cycle: null,
    trial_expired: false,
    trial_expires_at: null,
    limits: { brands: 1, social_channels: 0 },
    features: { video: false, press_distribution: false },
    refetch: () => {},
  };
};

export const SubscriptionProvider = ({ children }) => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchLimits = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await fetch(`${API_URL}/api/subscription/limits`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setData(await res.json());
      }
    } catch (e) {
      console.error('subscription fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLimits();
    // Poll every 10s so admin package changes propagate quickly to users.
    const id = setInterval(fetchLimits, 10000);
    // Re-fetch whenever the tab regains focus — users switching between tabs
    // or returning from TrialEnd/Upgrade flows see updates immediately.
    const onFocus = () => fetchLimits();
    const onVisibility = () => { if (!document.hidden) fetchLimits(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchLimits]);

  const daysLeft = () => {
    if (!data?.trial_expires_at) return null;
    try {
      const ms = new Date(data.trial_expires_at).getTime() - Date.now();
      return Math.max(0, Math.ceil(ms / (24 * 3600 * 1000)));
    } catch { return null; }
  };

  const value = {
    loading,
    plan: data?.plan || null,
    billing_cycle: data?.billing_cycle || null,
    status: data?.status || 'active',
    trial_expired: !!data?.trial_expired,
    trial_start_date: data?.trial_start_date || null,
    trial_expires_at: data?.trial_expires_at || null,
    trial_days_left: daysLeft(),
    custom: !!data?.custom,
    limits: data?.limits || { brands: 1, social_channels: 0 },
    features: data?.features || { video: false, press_distribution: false },
    credits_total: data?.credits_total ?? 0,
    credits_used: data?.credits_used ?? 0,
    refetch: fetchLimits,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionProvider;
